import React, { FC } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface HwUnavailableProps {
  /**
   * Technical error detail shown inside the accordion. For RTK transport errors
   * (map server unreachable) pass a fixed string like "Onboarding server
   * unavailable". For server-supplied errors pass the `error` field from the
   * onboarding status response.
   */
  errorDetail?: string | null;
}

/**
 * Health-wallet unavailability screen. Rendered when either:
 *  - The RTK call to /onboarding/status fails entirely (map server unreachable).
 *  - /onboarding/status returns 200 but the state body carries `error` (admin
 *    downstream unavailable).
 *
 * Identical UX in both cases; the accordion exposes the underlying detail when
 * available. Styled to match the rounded white-card layout used elsewhere in
 * the onboarding flow (OnboardingLanding's Paper card).
 */
export const HwUnavailable: FC<HwUnavailableProps> = ({ errorDetail }) => {
  return (
    <Container component="main" maxWidth="sm" sx={{ mt: 6, pb: 8 }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 4,
          p: { xs: 3, md: 5 },
          bgcolor: 'white',
          boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#fff7ed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <BuildIcon sx={{ fontSize: 44, color: '#c2410c' }} />
        </Box>

        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: '#1c1c1e', mb: 1.5, textAlign: 'center' }}
        >
          Sorry, Health Wallet is currently unavailable
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, textAlign: 'center', maxWidth: 480 }}
        >
          We're sorry for the inconvenience. Please try again in a few minutes. If the problem persists, contact support.
        </Typography>

        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 600, px: 4, mb: errorDetail ? 3 : 0 }}
        >
          Try Again
        </Button>

        {errorDetail && (
          <Accordion sx={{ width: '100%', boxShadow: 'none', border: '1px solid #e5e7eb', borderRadius: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">
                Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="caption"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, color: 'text.secondary' }}
              >
                {errorDetail}
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>
    </Container>
  );
};
